import React from 'react';
import ReactDOM from 'react-dom/client';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { msalConfig, apiScopes } from './config/authConfig';
import { getAccessToken } from './services/authToken';
import { restoreReturnLocation } from './services/returnLocation';
import { queryClient, queryCachePersistOptions } from './queryClient';
import { ThemeProvider, ThemedFluentProvider } from './theme/ThemeContext';
import { installBuildStampGlobal } from './buildInfo';
import './index.css';

// BL-094 — `window.__PTC_BUILD__` answers "which commit is this bundle?" from a browser console,
// with no local build and no deploy record. Called before MSAL so it is available even if auth
// initialisation throws.
installBuildStampGlobal();

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Attach an Azure AD bearer token to every call to the Functions API. Done as a global
 * fetch interceptor (rather than per call site) so it covers the shared api client AND the
 * scattered inline fetches/uploads — every func-app request is authenticated uniformly.
 * If a token can't be acquired silently the request proceeds without one (the backend will
 * 401 once the gate is live; the login flow handles re-auth).
 */
function installApiAuthInterceptor(instance: PublicClientApplication) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url;
    if (API_BASE && url.startsWith(API_BASE)) {
      // Serialized acquisition with a full-page interactive-redirect fallback when
      // the silent-renew iframe times out (see services/authToken.ts). A null
      // token means no account, a transient failure, or a redirect is under way —
      // send unauthenticated and let the backend 401 / the login flow take over.
      const token = await getAccessToken(instance, apiScopes);
      if (token) {
        const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
        headers.set('Authorization', `Bearer ${token}`);
        init = { ...init, headers };
      }
    }
    return originalFetch(input as RequestInfo | URL, init);
  };
}

const msalInstance = new PublicClientApplication(msalConfig);

// Initialize MSAL and handle redirect
async function initializeMsal() {
  // Handle redirect promise first
  await msalInstance.initialize();

  const response = await msalInstance.handleRedirectPromise();

  if (response) {
    msalInstance.setActiveAccount(response.account);
    // SUP-02: we just returned from an interactive auth redirect (Azure AD lands
    // on the app root). If authToken.ts stashed the route the user was on, put
    // it back before React mounts so the router boots on the original page.
    restoreReturnLocation();
  } else {
    // Set active account if one exists
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  // Listen for sign-in events
  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: any };
      msalInstance.setActiveAccount(payload.account);
    }
  });

  // Authenticate every Functions API call with a bearer token.
  installApiAuthInterceptor(msalInstance);

  // Render app after MSAL is ready
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <PersistQueryClientProvider client={queryClient} persistOptions={queryCachePersistOptions}>
          <ThemeProvider>
            <ThemedFluentProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </ThemedFluentProvider>
          </ThemeProvider>
        </PersistQueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

initializeMsal().catch(console.error);
